import React, { useState } from 'react';
import { 
  MessageSquare, 
  MessageSquareQuote, 
  Plus, 
  ArrowLeft, 
  Search, 
  TrendingUp, 
  ChevronRight,
  ShieldAlert,
  Users,
  Image as ImageIcon,
  Link as LinkIcon,
  Smile,
  Video,
  Send,
  Share2
} from 'lucide-react';
import { useDiscussion, useDiscussionComments, useCreateTopic, useCreateComment } from '../hooks/useDiscussions';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { useInfiniteQuery } from '@tanstack/react-query';
import { apiWorker, PaginatedResponse } from '../api/worker';
import { useSubNodes } from '../hooks/useNode';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { Avatar } from '../components/ui/Avatar';
import { CommentNode } from '../components/CommentNode';
import { RichContentViewer } from '../components/RichContentViewer';
import { safeDistanceToNow, cn } from '../lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '../components/ui/EmptyState';
import toast from 'react-hot-toast';

export const DiscussionsPage = () => {
  const { node, getTerm } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTopicId = searchParams.get('topic');
  const [selectedTopicId, setSelectedTopicId] = React.useState<string | null>(initialTopicId);
  const [isCreatingTopic, setIsCreatingTopic] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const { 
    data, 
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['discussions', node?.id, 'infinite'],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const targetId = node?.id && node.id !== 'platform-root' ? node.id : 'global';
      const response = await apiWorker.get<PaginatedResponse<any>>(`/api/nodes/${targetId}/topics?page=${pageParam}&size=15`);
      return response.data;
    },
    getNextPageParam: (lastPage: PaginatedResponse<any>) => lastPage.last ? undefined : lastPage.number + 1,
    enabled: !!node?.id
  });

  const rawPages = Array.isArray(data?.pages) ? data.pages : [];
  const topics = rawPages.flatMap(page => (page && Array.isArray(page.content)) ? page.content : []);

  // Sync state with URL if needed, but primarily used for deep linking.
  // Render-phase adjustment keyed on the deep-link value; also keeps behavior when
  // initialTopicId later changes to a different value.
  const [prevInitialTopicId, setPrevInitialTopicId] = useState<string | null | undefined>(null);
  if (initialTopicId && initialTopicId !== selectedTopicId && initialTopicId !== prevInitialTopicId) {
    setPrevInitialTopicId(initialTopicId);
    setSelectedTopicId(initialTopicId);
  }

  const handleSelectTopic = (id: string | null) => {
    setSelectedTopicId(id);
    if (id) {
      setSearchParams({ topic: id });
    } else {
      setSearchParams({});
    }
  };

  if (isLoading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-navy-900 dark:text-white tracking-tight flex items-center gap-3">
            <MessageSquareQuote size={32} className="text-brand-primary" />
            {getTerm('discussions')}
          </h1>
          <p className="text-gray-500 text-sm mt-1">Hierarchical discussions and community feedback</p>
        </div>
        {!selectedTopicId && !isCreatingTopic && (
          <Button onClick={() => setIsCreatingTopic(true)}>
            <Plus size={18} className="mr-2" />
            Start Discussion
          </Button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {isCreatingTopic ? (
          <CreateTopicForm onCancel={() => setIsCreatingTopic(false)} />
        ) : selectedTopicId ? (
          <TopicDetailView 
            topicId={selectedTopicId} 
            onBack={() => handleSelectTopic(null)} 
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search discussions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white border-2 border-black dark:border-white rounded-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none transition-all shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900"
                />
              </div>

              {(!topics || topics.length === 0) ? (
                  <EmptyState
                  icon={MessageSquareQuote}
                  title="No Discussions Yet"
                  description={`Be the first to start a conversation in ${node?.name}. Discussions are threaded and support rich content.`}
                  actions={[{ label: 'Start Discussion', onClick: () => setIsCreatingTopic(true) }]}
                  tips={[
                    'Discussions support markdown formatting, embedded images, and video links',
                    'Enable "Share with child organizations" to cascade threads to sub-groups',
                    'Use threaded replies to keep conversations organized',
                  ]}
                />
              ) : (
                <div className="space-y-4">
                  {topics.filter(t => {
                    if (!t) return false;
                    const q = searchQuery.trim().toLowerCase();
                    if (!q) return true;
                    return (
                      (t.title || '').toLowerCase().includes(q) ||
                      (t.content || '').toLowerCase().includes(q) ||
                      (t.authorName || '').toLowerCase().includes(q) ||
                      (t.nodeName || '').toLowerCase().includes(q)
                    );
                  }).map((topic) => (
                    <TopicCard 
                      key={topic.id} 
                      topic={topic} 
                      onClick={() => handleSelectTopic(topic.id)} 
                    />
                  ))}

                  {hasNextPage && (
                    <div className="pt-6 pb-2 flex justify-center">
                      <Button 
                        variant="secondary" 
                        onClick={() => fetchNextPage()} 
                        isLoading={isFetchingNextPage}
                      >
                        Load More Discussions
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-6">
              <Card className="p-6">
                <h3 className="font-bold text-navy-900 dark:text-white mb-4 flex items-center gap-2">
                  <TrendingUp size={18} className="text-brand-primary" />
                  Trending Topics
                </h3>
                <div className="space-y-4">
                  {(topics || []).slice(0, 3).map(t => (
                    <div key={t.id} className="group cursor-pointer" onClick={() => handleSelectTopic(t.id)}>
                      <p className="text-sm font-bold text-gray-700 group-hover:text-brand-primary transition-colors line-clamp-1">{t.title}</p>
                      <span className="text-[10px] text-gray-400 font-mono uppercase tracking-widest">{t.commentCount} comments</span>
                    </div>
                  ))}
                </div>
              </Card>

              <div className="bg-brand-primary/5 rounded-none p-6 border-2 border-black dark:border-white border-brand-primary/10">
                <h3 className="font-bold text-navy-900 dark:text-white mb-2 flex items-center gap-2">
                  <ShieldAlert size={16} className="text-brand-primary" />
                  Inheritance
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                  You are seeing discussions from <strong>{node?.name}</strong> and public threads cascaded down from parent organizations.
                </p>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TopicCard = ({ topic, onClick }: { topic: any, onClick: () => void, key?: any }) => {
  const { node } = useTenant();
  const isFromParent = topic.nodeId !== node?.id;
  const isCascaded = topic.cascadesDown && isFromParent;
  const isPrivate = topic.isPrivate;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      layout
    >
      <Card 
        onClick={onClick}
        className="p-5 hover:border-brand-primary/30 cursor-pointer transition-all hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] group"
      >
        <div className="flex gap-4">
          <div className="flex flex-col items-center gap-1 mt-1">
             <div className="w-10 h-10 bg-white rounded-none flex items-center justify-center text-gray-400 group-hover:bg-brand-primary/10 group-hover:text-brand-primary transition-colors dark:bg-slate-950">
               <MessageSquare size={20} />
             </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
               {isFromParent && (
                 <Badge variant="blue" className="text-[10px] uppercase font-mono tracking-widest bg-brand-primary/10 text-brand-primary border-none">
                    Parent Org
                 </Badge>
               )}
               {isCascaded && (
                 <Badge variant="green" className="text-[10px] uppercase font-mono tracking-widest bg-green-100 text-green-700 border-none dark:bg-green-900/30 dark:text-green-300">
                    Cascaded
                 </Badge>
               )}
               {isPrivate && (
                 <Badge variant="red" className="text-[10px] uppercase font-mono tracking-widest bg-red-100 text-red-700 border-none dark:bg-red-900/30 dark:text-red-300">
                    Private
                 </Badge>
               )}
               <span className="text-[10px] text-gray-400 font-mono uppercase tracking-widest">
                 Posted in {topic.nodeName} • {safeDistanceToNow(topic.createdAt)} ago
               </span>
            </div>
            <h3 className="text-lg font-bold text-navy-900 dark:text-white group-hover:text-brand-primary transition-colors mb-2 leading-tight">
              {topic.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4 leading-relaxed">
              {topic.content}
            </p>
            <div className="flex items-center gap-4 text-xs font-bold text-gray-400 uppercase tracking-widest font-mono">
              <span className="flex items-center gap-1.5 hover:text-navy-900 dark:text-white transition-colors">
                 <MessageSquareQuote size={14} />
                 {topic.commentCount} Comments
              </span>
              <span className="flex items-center gap-1.5 hover:text-navy-900 dark:text-white transition-colors">
                 <Users size={14} />
                 {topic.authorName}
              </span>
            </div>
          </div>
          <ChevronRight className="text-gray-200 group-hover:text-brand-primary transition-colors flex-shrink-0" size={24} />
        </div>
      </Card>
    </motion.div>
  );
};

const CreateTopicForm = ({ onCancel }: { onCancel: () => void, key?: any }) => {
  const { node } = useTenant();
  const { data: subNodes } = useSubNodes(node?.id || '');
  const hasChildren = (subNodes?.length || 0) > 0;
  const [title, setTitle] = React.useState('');
  const [content, setContent] = React.useState('');
  const [visibility, setVisibility] = React.useState<'node' | 'cascade' | 'private'>('node');
  const createTopic = useCreateTopic();

  const isFreeTier = node?.tier === 'LOCAL';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !content) return;
    await createTopic.mutateAsync({
      title,
      content,
      cascadesDown: visibility === 'cascade',
      isPrivate: visibility === 'private',
    });
    onCancel();
  };

  const visibilityOptions = [
    { value: 'node', label: 'Current node only', description: 'Visible to members of this organization only' },
    ...(hasChildren ? [{ value: 'cascade', label: 'Share with child organizations', description: 'Cascades down to all sub-groups in your hierarchy' }] : []),
    { value: 'private', label: 'Private (leadership only)', description: 'Visible only to members with moderation permissions' },
  ];

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
      <Card className="p-8 max-w-2xl mx-auto shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-t-4 border-brand-primary">
        <div className="flex items-center gap-4 mb-8">
           <button onClick={onCancel} className="p-2 hover:bg-white rounded-none transition-colors dark:bg-slate-800">
             <ArrowLeft size={24} />
           </button>
           <h2 className="text-2xl font-black text-navy-900 dark:text-white tracking-tight text-center flex-1 pr-12">Start a New Discussion</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-gray-400 font-mono">Topic Title</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What do you want to talk about?"
              className="w-full text-xl font-bold border-b-2 border-black dark:border-white focus:border-brand-primary outline-none py-2 transition-all placeholder:text-gray-200"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-widest text-gray-400 font-mono">Discussion Content</label>
              <div className="flex items-center gap-1">
                {!isFreeTier && (
                  <>
                    <button type="button" title="Add Image" onClick={() => setContent(prev => prev + '![image](https://)')} className="p-1 hover:bg-white rounded text-gray-400 hover:text-brand-primary dark:bg-slate-800"><ImageIcon size={14} /></button>
                    <button type="button" title="Add Video" onClick={() => setContent(prev => prev + 'https://www.youtube.com/watch?v=')} className="p-1 hover:bg-white rounded text-gray-400 hover:text-brand-primary dark:bg-slate-800"><Video size={14} /></button>
                  </>
                )}
                <button type="button" title="Add Link" onClick={() => setContent(prev => prev + '[title](https://)')} className="p-1 hover:bg-white rounded text-gray-400 hover:text-brand-primary dark:bg-slate-800"><LinkIcon size={14} /></button>
                {!isFreeTier && (
                  <button type="button" title="Add GIF" onClick={() => setContent(prev => prev + '![GIF](https://)')} className="p-1 hover:bg-white rounded text-gray-400 hover:text-brand-primary dark:bg-slate-800"><Smile size={14} /></button>
                )}
              </div>
            </div>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Explain the background or ask a question. Supports Markdown, links, and auto-video embedding..."
              className="w-full min-h-[200px] bg-white rounded-none p-4 text-gray-700 outline-none focus:ring-2 focus:ring-brand-primary/10 transition-all border-2 border-black dark:border-white border-transparent focus:border-brand-primary/20 dark:bg-slate-950"
            />
          </div>

          <div className="space-y-3">
            <label className="text-xs font-bold uppercase tracking-widest text-gray-400 font-mono">Visibility</label>
            <div className="space-y-2">
              {visibilityOptions.map(opt => (
                <label key={opt.value} className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="visibility"
                    value={opt.value}
                    checked={visibility === opt.value}
                    onChange={(e) => setVisibility(e.target.value as typeof visibility)}
                    className="mt-1 w-4 h-4 text-brand-primary focus:ring-brand-primary border-gray-300"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-brand-primary transition-colors">
                      {opt.label}
                    </p>
                    <p className="text-[10px] text-gray-400 uppercase font-mono tracking-widest italic mt-0.5">
                      {opt.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
            {!hasChildren && (
              <p className="text-[10px] text-gray-400 italic">
                Create sub-groups to enable cascading discussions to child organizations
              </p>
            )}
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel} className="flex-1">Cancel</Button>
            <Button type="submit" className="flex-1" isLoading={createTopic.isPending}>Post Discussion</Button>
          </div>
        </form>
      </Card>
    </motion.div>
  );
};

const TopicDetailView = ({ topicId, onBack }: { topicId: string, onBack: () => void, key?: any }) => {
  const { node, getTerm } = useTenant();
  const { user } = useCurrentUser();
  const { data: topic, isLoading: isTopicLoading } = useDiscussion(topicId);
  const { data: comments, isLoading: isCommentsLoading } = useDiscussionComments(topicId);
  const [commentContent, setCommentContent] = React.useState('');
  const [sortMode, setSortMode] = React.useState<'newest' | 'top' | 'oldest'>('newest');
  const createComment = useCreateComment();
  const composerRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const scrollToComposer = () => {
    composerRef.current?.scrollIntoView({ behavior: 'smooth' });
    textareaRef.current?.focus();
  };

  const isFreeTier = node?.tier === 'LOCAL';
  const isCascaded = topic?.cascadesDown && topic?.nodeId !== node?.id;
  const isPrivate = topic?.isPrivate;

  const handlePostComment = async () => {
    if (!commentContent.trim()) return;
    await createComment.mutateAsync({
      topicId,
      content: commentContent,
    });
    setCommentContent('');
  };

  const commentList = Array.isArray(comments) ? comments : [];

  const sortedComments = React.useMemo(() => {
    const list = [...commentList];
    if (sortMode === 'newest') {
      return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    if (sortMode === 'oldest') {
      return list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
    if (sortMode === 'top') {
      return list.sort((a, b) => (b.replies?.length || 0) - (a.replies?.length || 0));
    }
    return list;
  }, [commentList, sortMode]);

  if (isTopicLoading) return <div className="p-12 flex justify-center"><Spinner /></div>;
  if (!topic) return <div className="p-8 text-center text-gray-500 font-mono">Topic not found</div>;

  const currentUserName = user?.fullName || user?.firstName || user?.username || 'Community Member';

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="max-w-4xl mx-auto space-y-6">
      {/* Navigation & Badges */}
      <div className="flex items-center justify-between gap-3">
        <button 
          onClick={onBack} 
          className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-black dark:border-white bg-white dark:bg-slate-900 text-black dark:text-white font-mono text-xs uppercase font-bold tracking-wider hover:bg-brand-primary hover:text-white transition-colors shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
        >
          <ArrowLeft size={14} /> Back to {getTerm('discussions')}
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="blue" className="bg-brand-primary/10 text-brand-primary border-none uppercase text-[10px] font-mono tracking-widest">
            {topic.nodeName}
          </Badge>
          {isCascaded && (
            <Badge variant="green" className="bg-green-100 text-green-700 border-none uppercase text-[10px] font-mono tracking-widest dark:bg-green-900/30 dark:text-green-300">
              Cascaded
            </Badge>
          )}
          {isPrivate && (
            <Badge variant="red" className="bg-red-100 text-red-700 border-none uppercase text-[10px] font-mono tracking-widest dark:bg-red-900/30 dark:text-red-300">
              Private
            </Badge>
          )}
        </div>
      </div>

      {/* Main Original Topic Card */}
      <div className="bg-white dark:bg-slate-900 border-2 border-black dark:border-white p-6 md:p-8 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] rounded-none">
        {/* Author Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-slate-800 mb-5 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar userId={topic.authorUserId} initials={topic.authorName} size="md" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-black text-navy-900 dark:text-white text-base truncate">{topic.authorName}</span>
                <span className="px-1.5 py-0.5 text-[9px] font-black uppercase font-mono tracking-widest bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
                  Author
                </span>
              </div>
              <p className="text-xs text-gray-400 font-mono uppercase tracking-wider mt-0.5">
                Posted {safeDistanceToNow(topic.createdAt)} ago
              </p>
            </div>
          </div>

          <button
            onClick={() => {
              navigator.clipboard?.writeText(window.location.href);
              toast.success('Link copied to clipboard!');
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-mono font-bold text-gray-500 hover:text-brand-primary transition-colors border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 shrink-0"
            title="Copy thread link"
          >
            <Share2 size={13} />
            <span className="hidden sm:inline">Share</span>
          </button>
        </div>

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-navy-900 dark:text-white tracking-tight leading-tight mb-4 font-display">
          {topic.title}
        </h1>

        {/* Content Body */}
        <div className="text-gray-800 dark:text-gray-200 text-base leading-relaxed py-2">
          <RichContentViewer content={topic.content} />
        </div>

        {/* Post Footer */}
        <div className="flex items-center justify-between pt-5 mt-6 border-t border-gray-100 dark:border-slate-800 text-xs font-mono text-gray-400 gap-3">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 font-bold text-gray-600 dark:text-gray-300">
              <MessageSquareQuote size={15} className="text-brand-primary" />
              {topic.commentCount || commentList.length} Comments
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={scrollToComposer}
              className="inline-flex items-center gap-1.5 px-3 py-1 font-mono font-bold text-xs uppercase tracking-wider text-black dark:text-white bg-gray-50 dark:bg-slate-800 hover:bg-brand-primary hover:text-white transition-colors border border-black/10 dark:border-white/10"
            >
              <Send size={12} />
              Write a comment
            </button>
            <span className="text-[10px] tracking-widest uppercase text-gray-400 hidden sm:inline">
              {topic.nodeName} Community Board
            </span>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="space-y-4">
        {/* Header & Sort Controls */}
        <div className="flex items-center justify-between pb-3 border-b-2 border-black dark:border-white">
          <div className="flex items-center gap-2">
            <MessageSquareQuote size={18} className="text-brand-primary" />
            <h3 className="font-mono text-xs sm:text-sm font-black uppercase tracking-widest text-navy-900 dark:text-white">
              Comments ({commentList.length})
            </h3>
          </div>

          <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 p-0.5 border border-black/10 dark:border-white/10 font-mono text-[11px] font-bold">
            {(['newest', 'top', 'oldest'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setSortMode(mode)}
                className={cn(
                  "px-2.5 py-1 capitalize transition-all",
                  sortMode === mode
                    ? "bg-black text-white dark:bg-white dark:text-black shadow-sm"
                    : "text-gray-500 hover:text-black dark:hover:text-white"
                )}
              >
                {mode === 'top' ? 'Top Rated' : mode}
              </button>
            ))}
          </div>
        </div>

        {/* Comments Feed */}
        {isCommentsLoading ? (
          <div className="py-12 flex justify-center"><Spinner /></div>
        ) : sortedComments.length === 0 ? (
          <div className="text-center py-12 px-4 bg-gray-50 dark:bg-slate-900/50 border-2 border-dashed border-gray-200 dark:border-slate-800">
            <MessageSquare size={36} className="text-gray-300 dark:text-slate-600 mx-auto mb-3" />
            <h4 className="font-bold text-sm text-navy-900 dark:text-white mb-1">No comments yet</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              Be the first to share your thoughts below and start the conversation!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedComments.map((comment) => (
              <CommentNode 
                key={comment.id} 
                comment={comment} 
                topicId={topicId}
                opAuthorId={topic.authorUserId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Inviting Comment Composer (Positioned Below Comments) */}
      <div 
        ref={composerRef}
        className="bg-white dark:bg-slate-900 border-2 border-black dark:border-white p-5 sm:p-6 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] rounded-none mt-8"
      >
        <div className="flex items-center gap-2.5 mb-3">
          <Avatar userId={user?.id || 'me'} initials={currentUserName} size="sm" />
          <span className="text-xs font-bold uppercase tracking-wider font-mono text-gray-600 dark:text-gray-300">
            Leave a comment as <span className="text-brand-primary">{currentUserName}</span>
          </span>
          <div className="flex-1" />
          <span className="text-[10px] text-gray-400 font-mono uppercase tracking-widest hidden sm:inline">
            Markdown supported
          </span>
        </div>

        <textarea
          ref={textareaRef}
          value={commentContent}
          onChange={(e) => setCommentContent(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              handlePostComment();
            }
          }}
          placeholder="What are your thoughts? Ask a question, share an update, or join the discussion..."
          className="w-full min-h-[90px] sm:min-h-[100px] bg-gray-50 dark:bg-slate-950 border-2 border-black/10 dark:border-white/10 focus:border-brand-primary dark:focus:border-brand-primary outline-none p-3.5 text-sm transition-all text-navy-900 dark:text-white placeholder:text-gray-400"
        />

        <div className="flex items-center justify-between gap-3 mt-3 pt-2">
          <div className="flex items-center gap-1.5 text-gray-400">
            <button 
              type="button" 
              title="Add Link" 
              onClick={() => setCommentContent(prev => prev + '[link title](https://)')} 
              className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 hover:text-brand-primary transition-colors text-xs font-mono font-bold flex items-center gap-1 border border-gray-200 dark:border-slate-800"
            >
              <LinkIcon size={13} /> <span className="hidden sm:inline">Link</span>
            </button>
            {!isFreeTier && (
              <button 
                type="button" 
                title="Add Image" 
                onClick={() => setCommentContent(prev => prev + '![image](https://)')} 
                className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 hover:text-brand-primary transition-colors text-xs font-mono font-bold flex items-center gap-1 border border-gray-200 dark:border-slate-800"
              >
                <ImageIcon size={13} /> <span className="hidden sm:inline">Image</span>
              </button>
            )}
            <span className="text-[10px] text-gray-400 font-mono ml-2 hidden md:inline">
              Press ⌘+Enter to post
            </span>
          </div>

          <Button 
            onClick={handlePostComment}
            isLoading={createComment.isPending}
            disabled={!commentContent.trim()}
            className="px-6 py-2.5 font-bold text-xs uppercase tracking-wider font-mono gap-2"
          >
            <Send size={14} />
            Post Comment
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
